import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Briefcase,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Download,
  FileText,
  Image as ImageIcon,
  Key,
  Loader2,
  LogOut,
  Mail,
  Phone,
  RefreshCw,
  Search,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "tvcabsat:admin-token";
const PAGE_SIZE = 25;

const readToken = () => {
  try {
    return window.localStorage.getItem(TOKEN_KEY) || "";
  } catch {
    return "";
  }
};

const writeToken = (value) => {
  try {
    if (value) window.localStorage.setItem(TOKEN_KEY, value);
    else window.localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Storage unavailable (private mode) — token won't persist; non-fatal.
  }
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-PT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const TokenGate = ({ onValid }) => {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!value.trim()) return;
    setBusy(true);
    try {
      // Probe with a 1-row query to validate the token before persisting it.
      await axios.get(`${API}/admin/careers/applications?limit=1`, {
        headers: { "X-Admin-Token": value.trim() },
      });
      writeToken(value.trim());
      onValid(value.trim());
      toast.success("Sessão administrativa iniciada.");
    } catch (err) {
      const code = err?.response?.status;
      toast.error(code === 401 ? "Token inválido." : "Servidor indisponível.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto mt-20 max-w-md px-6">
      <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-xl">
        <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-100 text-blue-700">
          <ShieldCheck className="h-6 w-6" strokeWidth={2.4} />
        </div>
        <h1 className="font-heading mt-4 text-2xl font-black tracking-tight text-slate-900">
          Painel de RH — Carreiras
        </h1>
        <p className="mt-2 text-sm text-slate-600">
          Insira o <span className="font-bold">ADMIN_TOKEN</span> para consultar
          as candidaturas recebidas.
        </p>
        <p className="mt-1 text-[11px] text-slate-500">
          Este é o token administrativo do site, definido na variável de ambiente{" "}
          <code className="rounded bg-slate-100 px-1 py-0.5 font-mono">ADMIN_TOKEN</code>.
          Não confunda com a chave do Resend ou qualquer outra API.
        </p>
        <form onSubmit={submit} className="mt-6 space-y-3" data-testid="admin-token-form">
          <div className="relative">
            <Key
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
              strokeWidth={2.4}
            />
            <input
              type="password"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="X-Admin-Token"
              autoFocus
              data-testid="admin-token-input"
              className="w-full rounded-lg border border-slate-300 bg-white py-3 pl-10 pr-3 text-sm text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            />
          </div>
          <button
            type="submit"
            disabled={busy || !value.trim()}
            data-testid="admin-token-submit"
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-blue-600 px-5 py-3 text-sm font-bold text-white shadow-md transition-all hover:-translate-y-0.5 hover:bg-blue-700 disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Entrar
          </button>
        </form>
      </div>
    </div>
  );
};

const FilterBar = ({ filters, setFilters, onReset, onRefresh, onExport, busy, exporting }) => {
  const update = (key) => (e) =>
    setFilters((f) => ({ ...f, [key]: e.target.value, page: 0 }));
  return (
    <div className="grid grid-cols-1 gap-3 rounded-2xl border border-slate-200 bg-white p-4 md:grid-cols-6">
      <div className="relative md:col-span-2">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          value={filters.q}
          onChange={update("q")}
          placeholder="Nome / email / mensagem"
          data-testid="admin-filter-q"
          className="w-full rounded-lg border border-slate-300 bg-white py-2.5 pl-9 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
        />
      </div>
      <input
        value={filters.reference}
        onChange={update("reference")}
        placeholder="TVC-2026-001"
        data-testid="admin-filter-reference"
        className="rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
      />
      <input
        value={filters.role}
        onChange={update("role")}
        placeholder="Função"
        data-testid="admin-filter-role"
        className="rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
      />
      <input
        type="date"
        value={filters.date_from}
        onChange={update("date_from")}
        data-testid="admin-filter-date-from"
        className="rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
      />
      <input
        type="date"
        value={filters.date_to}
        onChange={update("date_to")}
        data-testid="admin-filter-date-to"
        className="rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
      />
      <div className="flex gap-2 md:col-span-6">
        <button
          type="button"
          onClick={onRefresh}
          disabled={busy}
          data-testid="admin-refresh"
          className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-4 py-2 text-xs font-bold text-white transition-all hover:bg-slate-800 disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" strokeWidth={2.5} />}
          Atualizar
        </button>
        <button
          type="button"
          onClick={onExport}
          disabled={exporting || busy}
          data-testid="admin-export-csv"
          className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-4 py-2 text-xs font-bold text-white transition-all hover:bg-emerald-700 disabled:opacity-60"
        >
          {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" strokeWidth={2.5} />}
          Exportar CSV
        </button>
        <button
          type="button"
          onClick={onReset}
          data-testid="admin-reset"
          className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-4 py-2 text-xs font-semibold text-slate-700 transition-all hover:border-slate-500"
        >
          Limpar filtros
        </button>
      </div>
    </div>
  );
};

const Row = ({ item, expanded, onToggle }) => (
  <>
    <tr
      onClick={onToggle}
      data-testid={`admin-row-${item.reference}`}
      className="cursor-pointer border-b border-slate-100 transition-colors hover:bg-blue-50/40"
    >
      <td className="px-4 py-3 font-mono text-[12px] font-bold text-blue-700">
        {item.reference}
      </td>
      <td className="px-4 py-3 text-sm font-semibold text-slate-900">{item.name}</td>
      <td className="px-4 py-3 text-sm text-slate-700">{item.role || "—"}</td>
      <td className="px-4 py-3 text-[12.5px] text-slate-600">{formatDate(item.received_at)}</td>
      <td className="px-4 py-3 text-[12.5px] text-slate-600">
        <a href={`mailto:${item.email}`} className="hover:text-blue-700">{item.email}</a>
      </td>
      <td className="px-4 py-3 text-[12.5px] text-slate-600">{item.phone || "—"}</td>
      <td className="px-4 py-3 text-center text-xs font-bold text-slate-500">
        {(item.files || []).length} 📎
      </td>
    </tr>
    {expanded && (
      <tr className="bg-slate-50/60">
        <td colSpan={7} className="px-4 py-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="md:col-span-2">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">Mensagem</div>
              <p className="mt-1.5 whitespace-pre-wrap text-sm text-slate-800">
                {item.message?.trim() || <span className="text-slate-400">— (vazio)</span>}
              </p>
              <div className="mt-3 grid grid-cols-2 gap-2 text-[12px] text-slate-600">
                {item.location && <div>📍 {item.location}</div>}
                {item.experience && <div>🎯 {item.experience}</div>}
                {item.education && <div>🎓 {item.education}</div>}
                {item.linkedin && (
                  <div>
                    🔗{" "}
                    <a href={item.linkedin} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline">
                      LinkedIn
                    </a>
                  </div>
                )}
              </div>
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">Anexos</div>
              <ul className="mt-2 space-y-1.5">
                {(item.files || []).map((f) => (
                  <li key={f.filename} className="flex items-center gap-2 text-[12.5px] text-slate-700">
                    {f.content_type?.startsWith("image/") ? (
                      <ImageIcon className="h-3.5 w-3.5 text-blue-600" strokeWidth={2.4} />
                    ) : (
                      <FileText className="h-3.5 w-3.5 text-rose-600" strokeWidth={2.4} />
                    )}
                    <span className="truncate">{f.filename}</span>
                    <span className="ml-auto text-[10px] text-slate-400">{Math.round(f.size / 1024)} KB</span>
                  </li>
                ))}
                {(item.files || []).length === 0 && (
                  <li className="text-[12px] text-slate-400">Sem anexos.</li>
                )}
              </ul>
              <p className="mt-2 text-[10.5px] italic text-slate-400">
                Os ficheiros chegam ao RH por email — esta vista lista só metadados.
              </p>
            </div>
          </div>
        </td>
      </tr>
    )}
  </>
);

export default function AdminCareers() {
  const [token, setToken] = useState(() => readToken());
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [filters, setFilters] = useState({
    q: "",
    reference: "",
    role: "",
    date_from: "",
    date_to: "",
    page: 0,
  });

  const fetchData = useCallback(async () => {
    if (!token) return;
    setBusy(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", String(PAGE_SIZE));
      params.set("skip", String(filters.page * PAGE_SIZE));
      if (filters.q) params.set("q", filters.q);
      if (filters.reference) params.set("reference", filters.reference);
      if (filters.role) params.set("role", filters.role);
      if (filters.date_from) params.set("date_from", filters.date_from);
      if (filters.date_to) params.set("date_to", filters.date_to);
      const { data } = await axios.get(
        `${API}/admin/careers/applications?${params.toString()}`,
        { headers: { "X-Admin-Token": token } },
      );
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      const code = err?.response?.status;
      if (code === 401) {
        toast.error("Sessão expirada — entre novamente.");
        writeToken("");
        setToken("");
      } else {
        toast.error("Falha ao carregar candidaturas.");
      }
    } finally {
      setBusy(false);
    }
  }, [token, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total]);

  const logout = () => {
    writeToken("");
    setToken("");
    setItems([]);
    setTotal(0);
    toast.success("Sessão terminada.");
  };

  const resetFilters = () =>
    setFilters({ q: "", reference: "", role: "", date_from: "", date_to: "", page: 0 });

  // Build a single CSV row with proper escaping: wrap every value in quotes
  // and double any existing quotes (RFC 4180). Newlines inside fields stay
  // quoted, which Excel and Google Sheets both handle correctly.
  const csvEscape = (val) => {
    const s = val == null ? "" : String(val);
    return `"${s.replace(/"/g, '""')}"`;
  };

  const exportCsv = async () => {
    if (!token) return;
    setExporting(true);
    try {
      // Fetch up to 200 (server max) matching the current filters. For larger
      // result sets we'd need to loop with skip — kept simple for now.
      const params = new URLSearchParams();
      params.set("limit", "200");
      params.set("skip", "0");
      if (filters.q) params.set("q", filters.q);
      if (filters.reference) params.set("reference", filters.reference);
      if (filters.role) params.set("role", filters.role);
      if (filters.date_from) params.set("date_from", filters.date_from);
      if (filters.date_to) params.set("date_to", filters.date_to);
      const { data } = await axios.get(
        `${API}/admin/careers/applications?${params.toString()}`,
        { headers: { "X-Admin-Token": token } },
      );
      const rows = data.items || [];
      if (rows.length === 0) {
        toast.error("Sem candidaturas para exportar.");
        return;
      }
      const headers = [
        "Referência",
        "Recebido",
        "Nome",
        "Email",
        "Telefone",
        "Função",
        "Localização",
        "Experiência",
        "Educação",
        "LinkedIn",
        "Mensagem",
        "Ficheiros",
      ];
      const lines = [headers.map(csvEscape).join(",")];
      rows.forEach((it) => {
        const filesStr = (it.files || [])
          .map((f) => `${f.filename} (${Math.round(f.size / 1024)} KB)`)
          .join(" | ");
        lines.push(
          [
            it.reference,
            it.received_at,
            it.name,
            it.email,
            it.phone,
            it.role,
            it.location,
            it.experience,
            it.education,
            it.linkedin,
            it.message,
            filesStr,
          ]
            .map(csvEscape)
            .join(","),
        );
      });
      // \uFEFF BOM keeps Excel from mangling Portuguese accents.
      const blob = new Blob([`\uFEFF${lines.join("\n")}`], {
        type: "text/csv;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const stamp = new Date().toISOString().slice(0, 10);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tvcabsat-candidaturas-${stamp}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exportadas ${rows.length} candidaturas.`);
    } catch (err) {
      const code = err?.response?.status;
      toast.error(code === 401 ? "Sessão expirada." : "Falha ao exportar.");
    } finally {
      setExporting(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <TokenGate onValid={setToken} />
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <section className="pb-16 pt-28 md:pt-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.28em] text-blue-700">
                <Briefcase className="h-3.5 w-3.5" strokeWidth={2.5} />
                Painel administrativo — RH
              </div>
              <h1 className="font-heading mt-2 text-3xl font-black tracking-tight text-slate-900 md:text-4xl">
                Candidaturas recebidas
              </h1>
              <p className="mt-1 text-sm text-slate-600">
                Total: <span className="font-bold text-slate-900">{total}</span>
                <span className="ml-3 text-slate-400">·</span>
                <a
                  href="/admin/contactos"
                  className="ml-3 inline-flex items-center gap-1 font-semibold text-blue-700 hover:underline"
                  data-testid="admin-careers-link-contacts"
                >
                  Ir para Contactos
                </a>
              </p>
            </div>
            <button
              type="button"
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-xs font-semibold text-slate-700 transition-all hover:border-rose-400 hover:text-rose-700"
            >
              <LogOut className="h-3.5 w-3.5" strokeWidth={2.4} />
              Sair
            </button>
          </div>

          <div className="mt-6">
            <FilterBar
              filters={filters}
              setFilters={setFilters}
              onReset={resetFilters}
              onRefresh={fetchData}
              onExport={exportCsv}
              busy={busy}
              exporting={exporting}
            />
          </div>

          <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-full" data-testid="admin-table">
                <thead className="bg-slate-50">
                  <tr className="text-left font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                    <th className="px-4 py-3">Ref.</th>
                    <th className="px-4 py-3">Candidato</th>
                    <th className="px-4 py-3">Função</th>
                    <th className="px-4 py-3 inline-flex items-center gap-1"><Calendar className="h-3 w-3" strokeWidth={2.5} /> Recebido</th>
                    <th className="px-4 py-3 inline-flex items-center gap-1"><Mail className="h-3 w-3" strokeWidth={2.5} /> Email</th>
                    <th className="px-4 py-3 inline-flex items-center gap-1"><Phone className="h-3 w-3" strokeWidth={2.5} /> Telefone</th>
                    <th className="px-4 py-3 text-center">Ficheiros</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 && !busy && (
                    <tr>
                      <td colSpan={7} className="px-4 py-12 text-center text-sm text-slate-500">
                        Sem candidaturas para os filtros atuais.
                      </td>
                    </tr>
                  )}
                  {items.map((it) => (
                    <Row
                      key={it.id}
                      item={it}
                      expanded={expanded === it.id}
                      onToggle={() => setExpanded((cur) => (cur === it.id ? null : it.id))}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {totalPages > 1 && (
            <div className="mt-5 flex items-center justify-between text-sm text-slate-600">
              <div>
                Página <span className="font-bold text-slate-900">{filters.page + 1}</span> de {totalPages}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={filters.page === 0}
                  onClick={() => setFilters((f) => ({ ...f, page: Math.max(0, f.page - 1) }))}
                  data-testid="admin-page-prev"
                  className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
                >
                  <ChevronLeft className="h-3.5 w-3.5" /> Anterior
                </button>
                <button
                  type="button"
                  disabled={filters.page + 1 >= totalPages}
                  onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}
                  data-testid="admin-page-next"
                  className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
                >
                  Próxima <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
